import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import * as S from './styles';
import { canModifyScheduledMessage } from 'utils/canModifyScheduledMessage';

import {
  SchedulePickerModal,
  ScheduleMessageModal
} from '../Chat/SendMessageContainer/components';

type EditMessageProps = { message: string, newSendAt: Date, id: string }

interface ScheduledMessageCardProps {
  userName: string;
  userAvatarUrl: string;
  scheduledTime: string | Date;
  message: string;
  id: string;
  errorStatus?: string;
  isError?: boolean;
  onEdit: (props: EditMessageProps) => void;
  onReschedule: (props: EditMessageProps) => void;
  onSend: (props: {id: string, sendAt: string | Date, message: string}) => void;
  onDelete: () => Promise<void>;
  onRetry: (props: {id: string, sendAt: string | Date, message: string}) => Promise<void>;
}

export const ScheduledMessageCard: React.FC<ScheduledMessageCardProps> = ({
  userName,
  userAvatarUrl,
  scheduledTime,
  message,
  id,
  isError,
  errorStatus,
  onEdit,
  onReschedule,
  onSend,
  onDelete,
  onRetry,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
      handleCloseMenu();
    }
  };

  function formatScheduledDate(input: string | Date): string {
    const date = new Date(input);
    const now = new Date();
    const isSameDay = date.toDateString() === now.toDateString();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const timeString = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    if (isSameDay) return `Hoje ${timeString}`;
    if (isTomorrow) return `Amanhã ${timeString}`;
    const dayOfWeek = date.toLocaleDateString('pt-BR', {
      weekday: 'long',
    });
    return `${capitalize(dayOfWeek)} ${timeString}`;
  }
  
  function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  const onSendMessage = ({ message, sendAt }: { message: string; sendAt: Date }) => {
    setShowModal(false);
    setShowSchedulePicker(false);
    onReschedule({ message, id, newSendAt: sendAt });
  };

  return (
    <S.ScheduleMessageCardContainer isError={isError}>
      <SchedulePickerModal
        isOpen={showSchedulePicker}
        setIsModalOpen={setShowSchedulePicker}
        onScheduleMessage={(props) => {
          setShowSchedulePicker(false);
          onReschedule({ message, id, newSendAt: props.sendAt });
        }}
      />
      <ScheduleMessageModal
        isOpen={showModal}
        setIsModalOpen={setShowModal}
        setIsScheduleModalOpen={setShowSchedulePicker}
        message={message}
        onSendMessage={onSendMessage}
      />
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={userAvatarUrl} alt={userName} sx={{ width: 32, height: 32 }} />
        <Box>
          {isError ? (
            <Typography variant="caption" color="text.secondary">
              Falha erro ({errorStatus})
              <Box
                component="span"
                sx={{ color: '#007AFF', cursor: 'pointer', ml: 0.5, fontWeight: 500 }}
                onClick={() => onRetry({id, sendAt: scheduledTime, message})}
              >
                Tentar novamente.
              </Box>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Enviar {formatScheduledDate(scheduledTime)}
            </Typography>
          )}
          <Typography variant="body1" fontWeight="medium">{userName}</Typography>
          <Typography variant="body2" color="text.secondary">{message}</Typography>
        </Box>
      </Box>

      <Box>
        <IconButton onClick={handleOpenMenu}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            disabled={!canModifyScheduledMessage(scheduledTime).success}
            onClick={() => {
              onEdit({ message, id, newSendAt: new Date(scheduledTime) });
              handleCloseMenu();
            }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Editar Mensagem
          </MenuItem>
          <MenuItem disabled={!canModifyScheduledMessage(scheduledTime).success} onClick={() => { setShowModal(true); handleCloseMenu(); }}>
            <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
            Alterar data
          </MenuItem>
          <MenuItem onClick={() => { onSend({id, sendAt: scheduledTime, message}); handleCloseMenu(); }}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            Enviar
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleDelete}
            disabled={isDeleting || !canModifyScheduledMessage(scheduledTime).success}
          >
            {isDeleting ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={14} thickness={5} />
                </Box>
                Excluindo...
              </Box>
            ) : (
              <>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Excluir
              </>
            )}
          </MenuItem>
        </Menu>
      </Box>
    </S.ScheduleMessageCardContainer>
  );
};
